import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { SectionHeading } from "@/accounts-payable/common";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";
import { QuickbooksBillSettingsSection } from "./quickbooks-bill-settings-section";
import { QuickbooksConnectionStatus } from "./quickbooks-connection-status";
import { QuickbooksDisconnectDialog } from "./quickbooks-disconnect-dialog";
import type { BillSettingsDraft } from "./use-bill-settings-draft";
import { useQuickbooksAccess } from "./use-quickbooks-access";

const SettingsCard = ({ children }: { children: React.ReactNode }) => (
  <Card className="w-full gap-0 p-6">{children}</Card>
);

/**
 * AP's QuickBooks view, opened from the sidebar: the connection, and, once
 * connected, the bill settings. The OAuth callback lands here. Polls while the
 * account mirror is still syncing after connect.
 */
export const QuickbooksView = ({
  settings,
  onReviewVendors,
}: {
  /** Held by the AP page, so a draft survives switching views. */
  settings: BillSettingsDraft;
  onReviewVendors: () => void;
}) => {
  const { businessId, canManage } = useQuickbooksAccess();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const {
    data: connection,
    isPending,
    isError,
    refetch,
  } = useGetQuickbooksConnection(businessId, { pollWhileSyncing: true });

  if (!businessId) return null;

  const showBillSettings =
    canManage &&
    connection?.status === "connected" &&
    connection.bill_push_available;

  return (
    <div className="min-h-0 w-full flex-1 overflow-y-auto">
      <Stack align="start" gap={4} className="w-full max-w-2xl pt-2">
        <SettingsCard>
          <Stack className="w-full" align="start" gap={4}>
            <SectionHeading>Connection</SectionHeading>
            {isPending ? (
              <Stack direction="row" gap={2} align="center" role="status">
                <Spinner layout="centered" size="sm" />
                <Text tone="muted">Loading your QuickBooks connection...</Text>
              </Stack>
            ) : isError ? (
              <Stack gap={2} align="start" role="alert">
                <Text tone="destructive">
                  Couldn’t load the QuickBooks connection. Please try again.
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void refetch()}
                >
                  Try again
                </Button>
              </Stack>
            ) : (
              <QuickbooksConnectionStatus
                businessId={businessId}
                connection={connection}
                canManage={canManage}
                onDisconnect={() => setIsDisconnectOpen(true)}
              />
            )}
            {!canManage && (
              <Text tone="muted">
                Only managers and admins can manage the QuickBooks connection.
              </Text>
            )}
          </Stack>
        </SettingsCard>

        {showBillSettings && (
          <SettingsCard>
            <QuickbooksBillSettingsSection
              businessId={businessId}
              connection={connection}
              settings={settings}
              onReviewVendors={onReviewVendors}
            />
          </SettingsCard>
        )}
      </Stack>

      <QuickbooksDisconnectDialog
        businessId={businessId}
        open={isDisconnectOpen}
        onClose={() => setIsDisconnectOpen(false)}
      />
    </div>
  );
};
